import { useQuery } from '@tanstack/react-query';
import {
  AlarmClock,
  CalendarClock,
  CalendarDays,
  CalendarX,
  Hourglass,
  LayoutGrid,
  List,
  MapPin,
  Pencil,
  Trophy,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelectedClub } from '@/features/club';
import {
  buildNameMap,
  buildScheduleTimeline,
  collectPlayerIds,
  courtAccent,
  roundAccent,
  roundLabel,
  ScheduleGrid,
  shortTime,
  toMinutes,
  useClubCompetitionsQuery,
  useClubCourtsQuery,
  useClubMatchesQuery,
  useMatchPlannerQuery,
  type ApiMatch,
  type ScheduleCourt,
  type ScheduleTimeline,
} from '@/features/competition';
import { getUsersByIds, userKeys } from '@/features/user';
import { cn } from '@/shared/lib/cn';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DateInput,
  EmptyState,
  IconButton,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/shared/ui';
import { ScheduleMatchDialog } from './scheduled/ScheduleMatchDialog';

/** Formatuje date ISO (yyyy-MM-dd) na zapis polski (dd.MM.yyyy). */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  if (!year || !month || !day) return iso;
  return `${day}.${month}.${year}`;
}

/** Data lokalna w formacie ISO (yyyy-MM-dd) — do porownania z terminem meczu. */
function localIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Klucz sortowania: mecze z terminem chronologicznie, bez terminu na koncu. */
function sortKey(match: ApiMatch): string {
  if (!match.scheduledDate) return '9999-99-99 99:99';
  return `${match.scheduledDate} ${match.scheduledTime ?? '99:99'}`;
}

/** Klucz grupy dziennej dla meczow bez terminu. */
const UNSCHEDULED = 'unscheduled';

/** Zakres czasowy filtra listy. */
type RangeFilter = 'all' | 'today' | 'tomorrow' | 'dayAfter' | 'nextWeek';

/** Widok zakladki: lista dzienna albo os czasu kortow. */
type ViewMode = 'list' | 'grid';

/**
 * Zaplanowane mecze — wszystkie mecze ze wszystkich turniejów klubu, które
 * jeszcze się nie rozpoczęły, pogrupowane po dniach („Dziś", „Jutro", data).
 * Nad tabelą kafelki statystyk i filtry (turniej, kort, zakres), w wierszu
 * szybka edycja terminu, a przełącznik widoku pokazuje oś czasu kortów
 * (tylko do odczytu — pełne planowanie w zakładce Infrastruktura).
 */
export function ScheduledMatchesPage() {
  const { t } = useTranslation('common');
  const { t: tt } = useTranslation('tournament');
  const navigate = useNavigate();
  const { clubId, isEmpty: hasNoClubs } = useSelectedClub();

  // „Za chwile" i „po czasie" liczone raz na render — wystarczajaco swieze,
  // bo dane meczow i tak przeladowuja sie przy wejsciu na strone.
  const now = new Date();
  const todayIso = localIsoDate(now);
  const tomorrowIso = localIsoDate(new Date(now.getTime() + 86_400_000));
  const dayAfterIso = localIsoDate(new Date(now.getTime() + 2 * 86_400_000));
  // Następny tydzień kalendarzowy: poniedziałek–niedziela po bieżącym tygodniu.
  const daysSinceMonday = (now.getDay() + 6) % 7;
  const nextWeekStartIso = localIsoDate(
    new Date(now.getTime() + (7 - daysSinceMonday) * 86_400_000),
  );
  const nextWeekEndIso = localIsoDate(
    new Date(now.getTime() + (13 - daysSinceMonday) * 86_400_000),
  );
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  const [view, setView] = useState<ViewMode>('list');
  const [tournamentFilter, setTournamentFilter] = useState('');
  const [courtFilter, setCourtFilter] = useState('');
  const [range, setRange] = useState<RangeFilter>('all');
  const [plannerDate, setPlannerDate] = useState(todayIso);
  const [editing, setEditing] = useState<ApiMatch | null>(null);

  const matchesQuery = useClubMatchesQuery(clubId, { status: 'SCHEDULED' });
  const competitionsQuery = useClubCompetitionsQuery(clubId);
  const courtsQuery = useClubCourtsQuery(clubId);
  // Os czasu pobiera planner tylko wtedy, gdy widok jest aktywny.
  const plannerQuery = useMatchPlannerQuery(view === 'grid' ? clubId : undefined, plannerDate);
  const planner = plannerQuery.data;

  const matches = useMemo(
    () => [...(matchesQuery.data ?? [])].sort((a, b) => sortKey(a).localeCompare(sortKey(b))),
    [matchesQuery.data],
  );

  // Filtry dzialaja na widoku listy; statystyki licza sie z pelnej listy.
  const filtered = useMemo(
    () =>
      matches.filter((match) => {
        if (tournamentFilter && match.competitionId !== tournamentFilter) return false;
        if (courtFilter && match.courtId !== courtFilter) return false;
        if (range === 'today' && match.scheduledDate !== todayIso) return false;
        if (range === 'tomorrow' && match.scheduledDate !== tomorrowIso) return false;
        if (range === 'dayAfter' && match.scheduledDate !== dayAfterIso) return false;
        if (
          range === 'nextWeek' &&
          (!match.scheduledDate ||
            match.scheduledDate < nextWeekStartIso ||
            match.scheduledDate > nextWeekEndIso)
        ) {
          return false;
        }
        return true;
      }),
    [
      matches,
      tournamentFilter,
      courtFilter,
      range,
      todayIso,
      tomorrowIso,
      dayAfterIso,
      nextWeekStartIso,
      nextWeekEndIso,
    ],
  );

  // Sekcje dzienne: mecze sa juz posortowane chronologicznie, wiec kolejnosc
  // wstawiania do mapy daje wlasciwa kolejnosc grup (bez terminu na koncu).
  const dayGroups = useMemo(() => {
    const map = new Map<string, ApiMatch[]>();
    for (const match of filtered) {
      const key = match.scheduledDate ?? UNSCHEDULED;
      const group = map.get(key);
      if (group) group.push(match);
      else map.set(key, [match]);
    }
    return [...map.entries()];
  }, [filtered]);

  // Nazwy turniejow do kolumny — mecz niesie tylko competitionId.
  const tournamentNames = useMemo(
    () => new Map((competitionsQuery.data ?? []).map((item) => [item.id, item.name])),
    [competitionsQuery.data],
  );

  // Nazwy kortow — mecz wskazuje kort samym identyfikatorem.
  const courtNames = useMemo(
    () =>
      new Map(
        (courtsQuery.data ?? []).map((court) => [
          court.id,
          court.name || t('scheduledMatchesPage.court', { number: court.number }),
        ]),
      ),
    [courtsQuery.data, t],
  );

  // Jeden batch nazwisk dla tabeli i osi czasu (planner moze niesc innych graczy).
  const plannerMatches = useMemo(
    () => (planner?.scheduledMatches ?? []).map((entry) => entry.match),
    [planner],
  );
  const playerIds = useMemo(
    () => collectPlayerIds([...matches, ...plannerMatches]).sort(),
    [matches, plannerMatches],
  );
  const playersQuery = useQuery({
    queryKey: [...userKeys.all, 'batch', playerIds.join(',')],
    queryFn: () => getUsersByIds(playerIds),
    enabled: playerIds.length > 0,
  });
  const names = useMemo(() => buildNameMap(playersQuery.data ?? []), [playersQuery.data]);

  // Kafelki statystyk — z pelnej listy, niezaleznie od filtrow.
  const todayCount = useMemo(
    () => matches.filter((match) => match.scheduledDate === todayIso).length,
    [matches, todayIso],
  );
  const unscheduledCount = useMemo(
    () => matches.filter((match) => !match.scheduledDate).length,
    [matches],
  );
  const overdueCount = useMemo(
    () =>
      matches.filter((match) => {
        if (!match.scheduledDate || !match.scheduledTime) return false;
        if (match.scheduledDate < todayIso) return true;
        return match.scheduledDate === todayIso && toMinutes(match.scheduledTime) < nowMinutes;
      }).length,
    [matches, todayIso, nowMinutes],
  );
  const busyCourtsToday = useMemo(
    () =>
      new Set(
        matches
          .filter((match) => match.scheduledDate === todayIso && match.courtId)
          .map((match) => match.courtId),
      ).size,
    [matches, todayIso],
  );
  const courtsTotal = courtsQuery.data?.length ?? 0;

  /** Stan startu meczu: „wkrotce" (do godziny), „opozniony" albo neutralny. */
  function startState(match: ApiMatch): 'soon' | 'overdue' | null {
    if (!match.scheduledDate || !match.scheduledTime) return null;
    if (match.scheduledDate < todayIso) return 'overdue';
    if (match.scheduledDate > todayIso) return null;
    const start = toMinutes(match.scheduledTime);
    if (start < nowMinutes) return 'overdue';
    return start - nowMinutes <= 60 ? 'soon' : null;
  }

  /** Naglowek sekcji dziennej: „Dziś", „Jutro" albo dzien tygodnia z data. */
  function dayLabel(key: string): string {
    if (key === UNSCHEDULED) return t('scheduledMatchesPage.unscheduled');
    if (key === todayIso) return t('scheduledMatchesPage.today');
    if (key === tomorrowIso) return t('scheduledMatchesPage.tomorrow');
    const [year, month, day] = key.split('-').map(Number);
    const weekday = new Intl.DateTimeFormat('pl-PL', { weekday: 'long' }).format(
      new Date(year ?? 0, (month ?? 1) - 1, day ?? 1),
    );
    return `${weekday.charAt(0).toLocaleUpperCase('pl-PL')}${weekday.slice(1)}, ${formatDate(key)}`;
  }

  /** Nazwiska jednej strony jako tekst — do osi czasu i opisu dialogu. */
  function sideText(ids: readonly string[]): string {
    if (ids.length === 0) return '—';
    return ids.map((id) => names.get(id) ?? '…').join(' / ');
  }

  /** Jedna strona meczu: kolorowe kolka z inicjalami + nazwiska (debel: dwa kolka). */
  function side(ids: readonly string[]) {
    if (ids.length === 0) return <span className="text-fg-subtle">—</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        {ids.map((id) => (
          <Avatar key={id} name={names.get(id) ?? '…'} size="xs" colorful />
        ))}
        <span>{sideText(ids)}</span>
      </span>
    );
  }

  /** Etykieta fazy: runda drabinki albo grupa. */
  function phaseOf(match: ApiMatch): string {
    if (match.groupNumber === 0) return tt('matchResult.finalGroup');
    if (match.groupNumber !== null) {
      return tt('matchResult.group', { number: match.groupNumber });
    }
    return roundLabel(match.round) ?? '—';
  }

  /** Korty do naglowka osi czasu. */
  const gridCourts: ScheduleCourt[] = useMemo(
    () =>
      (planner?.courts ?? []).map((court) => ({
        id: court.id,
        name: court.name || t('scheduledMatchesPage.court', { number: court.number }),
        ...(court.surface ? { surface: court.surface } : {}),
      })),
    [planner, t],
  );

  /** Proporcjonalna os czasu zbudowana z plannera na wybrany dzien. */
  const timeline: ScheduleTimeline = useMemo(() => {
    if (!planner) return { startMinutes: 0, endMinutes: 0, tiles: [], nowMinutes: null };
    return buildScheduleTimeline(planner, sideText, { todayIso, nowMinutes });
    // sideText zalezy wylacznie od mapy nazwisk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planner, names, todayIso, nowMinutes]);

  /** Klik w mecz na osi czasu — przejscie do wpisania wyniku. */
  function openMatchResult(matchId: string) {
    const competitionId = plannerMatches.find((match) => match.id === matchId)?.competitionId;
    if (competitionId) {
      void navigate(`/turnieje/${competitionId}/mecze/${matchId}/wynik`);
    }
  }

  if (hasNoClubs) {
    return (
      <EmptyState
        icon={<CalendarClock />}
        title={t('scheduledMatchesPage.noClubsTitle')}
        description={t('scheduledMatchesPage.noClubsDescription')}
        className="min-h-[24rem]"
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('scheduledMatchesPage.title')}
        description={t('scheduledMatchesPage.description')}
      />

      {/* Kafelki statystyk dnia */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile
          icon={<CalendarDays />}
          value={todayCount}
          label={t('scheduledMatchesPage.stats.today')}
        />
        <StatTile
          icon={<CalendarX />}
          value={unscheduledCount}
          label={t('scheduledMatchesPage.stats.unscheduled')}
        />
        <StatTile
          icon={<Hourglass />}
          value={overdueCount}
          label={t('scheduledMatchesPage.stats.overdue')}
        />
        <StatTile
          icon={<MapPin />}
          value={`${busyCourtsToday}/${courtsTotal}`}
          label={t('scheduledMatchesPage.stats.courtsToday')}
        />
      </div>

      {/* Pasek narzedzi: filtry listy albo wybor dnia osi czasu + przelacznik widoku */}
      <div className="flex flex-wrap items-center gap-3">
        {view === 'list' ? (
          <>
            <Select
              selectSize="sm"
              aria-label={t('scheduledMatchesPage.filters.tournament')}
              className="w-52"
              value={tournamentFilter}
              onChange={(event) => setTournamentFilter(event.target.value)}
            >
              <option value="">{t('scheduledMatchesPage.filters.allTournaments')}</option>
              {(competitionsQuery.data ?? []).map((competition) => (
                <option key={competition.id} value={competition.id}>
                  {competition.name}
                </option>
              ))}
            </Select>
            <Select
              selectSize="sm"
              aria-label={t('scheduledMatchesPage.filters.court')}
              className="w-44"
              value={courtFilter}
              onChange={(event) => setCourtFilter(event.target.value)}
            >
              <option value="">{t('scheduledMatchesPage.filters.allCourts')}</option>
              {(courtsQuery.data ?? []).map((court) => (
                <option key={court.id} value={court.id}>
                  {court.name || t('scheduledMatchesPage.court', { number: court.number })}
                </option>
              ))}
            </Select>
            <SegmentedControl
              size="sm"
              label={t('scheduledMatchesPage.filters.range')}
              value={range}
              onValueChange={setRange}
              options={[
                { value: 'all', label: t('scheduledMatchesPage.filters.rangeAll') },
                { value: 'today', label: t('scheduledMatchesPage.filters.rangeToday') },
                { value: 'tomorrow', label: t('scheduledMatchesPage.filters.rangeTomorrow') },
                { value: 'dayAfter', label: t('scheduledMatchesPage.filters.rangeDayAfter') },
                { value: 'nextWeek', label: t('scheduledMatchesPage.filters.rangeNextWeek') },
              ]}
            />
          </>
        ) : (
          <>
            <DateInput
              inputSize="sm"
              aria-label={t('scheduledMatchesPage.grid.day')}
              value={plannerDate}
              onChange={(event) => setPlannerDate(event.target.value)}
            />
            <Button size="sm" variant="secondary" onClick={() => setPlannerDate(todayIso)}>
              {t('scheduledMatchesPage.filters.rangeToday')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void navigate('/turnieje/infrastruktura')}
            >
              {t('scheduledMatchesPage.grid.openPlanner')}
            </Button>
          </>
        )}

        <SegmentedControl
          size="sm"
          className="ml-auto"
          label={t('scheduledMatchesPage.filters.view')}
          value={view}
          onValueChange={setView}
          options={[
            { value: 'list', label: t('scheduledMatchesPage.filters.viewList'), icon: <List /> },
            {
              value: 'grid',
              label: t('scheduledMatchesPage.filters.viewGrid'),
              icon: <LayoutGrid />,
            },
          ]}
        />
      </div>

      {/* Widok osi czasu — tylko do odczytu, pelne planowanie w Infrastrukturze */}
      {view === 'grid' && (
        <>
          {plannerQuery.isError && (
            <Alert tone="danger">{t('scheduledMatchesPage.loadError')}</Alert>
          )}
          {plannerQuery.isPending && <Skeleton className="h-64 w-full" />}
          {planner &&
            (timeline.tiles.length === 0 ? (
              <EmptyState
                icon={<CalendarClock />}
                title={dayLabel(plannerDate === todayIso ? todayIso : plannerDate)}
                description={t('scheduledMatchesPage.grid.emptyDay')}
                className="min-h-[16rem]"
              />
            ) : (
              <ScheduleGrid
                courts={gridCourts}
                timeline={timeline}
                onMatchClick={openMatchResult}
              />
            ))}
        </>
      )}

      {/* Widok listy */}
      {view === 'list' && (
        <>
          {matchesQuery.isError && (
            <Alert tone="danger">{t('scheduledMatchesPage.loadError')}</Alert>
          )}
          {matchesQuery.isPending && <Skeleton className="h-64 w-full" />}

          {matchesQuery.isSuccess &&
            (matches.length === 0 ? (
              <EmptyState
                icon={<CalendarClock />}
                title={t('scheduledMatchesPage.title')}
                description={t('scheduledMatchesPage.empty')}
                className="min-h-[20rem]"
              />
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={<CalendarClock />}
                title={t('scheduledMatchesPage.title')}
                description={t('scheduledMatchesPage.filters.noResults')}
                className="min-h-[16rem]"
              />
            ) : (
              <TableContainer>
                <Table className="[&_td]:px-3 [&_th]:px-3">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>
                        {t('scheduledMatchesPage.columns.tournament')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-32">
                        {tt('matchesTab.columns.phase')}
                      </TableHeaderCell>
                      <TableHeaderCell>{tt('matchesTab.columns.players')}</TableHeaderCell>
                      <TableHeaderCell className="w-32">
                        {t('scheduledMatchesPage.columns.time')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-36">
                        {t('scheduledMatchesPage.columns.court')}
                      </TableHeaderCell>
                      <TableHeaderCell className="w-12">
                        <span className="sr-only">{t('scheduledMatchesPage.actions.edit')}</span>
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dayGroups.map(([dayKey, dayMatches]) => (
                      <Fragment key={dayKey}>
                        <TableRow className="border-b-0 bg-surface-sunken hover:bg-surface-sunken">
                          <TableCell
                            colSpan={6}
                            className="py-2 text-xs font-semibold tracking-[0.04em] text-fg-strong uppercase"
                          >
                            {dayLabel(dayKey)}
                          </TableCell>
                        </TableRow>

                        {dayMatches.map((match) => {
                          const state = startState(match);
                          const phaseAccent =
                            match.groupNumber === null ? roundAccent(match.round) : null;
                          return (
                            <TableRow
                              key={match.id}
                              className="cursor-pointer transition-colors hover:bg-surface-hover"
                              onClick={() =>
                                void navigate(`/turnieje/${match.competitionId}/mecze`)
                              }
                            >
                              <TableCell className="text-sm font-semibold text-fg-strong">
                                {tournamentNames.get(match.competitionId ?? '') ?? '—'}
                              </TableCell>
                              <TableCell
                                className={cn(
                                  'text-sm font-medium whitespace-nowrap',
                                  phaseAccent ?? 'text-fg-muted',
                                )}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  {match.round === 'F' && match.groupNumber === null && (
                                    <Trophy className="size-3.5" aria-hidden="true" />
                                  )}
                                  {phaseOf(match)}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="inline-flex items-center text-sm text-fg">
                                  {side(match.team1Ids)}
                                  <span className="px-2 text-fg-subtle">vs</span>
                                  {side(match.team2Ids)}
                                </span>
                              </TableCell>

                              {dayKey === UNSCHEDULED ? (
                                <TableCell colSpan={2}>
                                  <Badge tone="neutral" size="sm">
                                    {t('scheduledMatchesPage.toSchedule')}
                                  </Badge>
                                </TableCell>
                              ) : (
                                <>
                                  <TableCell className="text-sm whitespace-nowrap">
                                    <span className="inline-flex items-center gap-1.5">
                                      <span
                                        className={cn(
                                          'tabular-nums',
                                          state === 'soon'
                                            ? 'font-semibold text-fg-strong'
                                            : 'text-fg',
                                        )}
                                      >
                                        {shortTime(match.scheduledTime) ?? '—'}
                                      </span>
                                      {state === 'soon' && (
                                        <Badge tone="success" size="sm">
                                          <AlarmClock className="size-3" aria-hidden="true" />
                                          {t('scheduledMatchesPage.startingSoon')}
                                        </Badge>
                                      )}
                                      {state === 'overdue' && (
                                        <Badge tone="warning" size="sm">
                                          {t('scheduledMatchesPage.overdue')}
                                        </Badge>
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    {match.courtId ? (
                                      <span
                                        className={cn(
                                          'inline-flex h-6 items-center rounded-full px-2.5 text-xs font-semibold whitespace-nowrap',
                                          courtAccent(match.courtId),
                                        )}
                                      >
                                        {courtNames.get(match.courtId) ?? '—'}
                                      </span>
                                    ) : (
                                      <span className="text-sm text-fg-subtle">—</span>
                                    )}
                                  </TableCell>
                                </>
                              )}

                              <TableCell className="text-right">
                                <IconButton
                                  icon={<Pencil />}
                                  label={t('scheduledMatchesPage.actions.edit')}
                                  size="sm"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditing(match);
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>

                <TableFooter>
                  {t('scheduledMatchesPage.footer', { count: filtered.length })}
                </TableFooter>
              </TableContainer>
            ))}
        </>
      )}

      {editing && (
        <ScheduleMatchDialog
          key={editing.id}
          open
          onClose={() => setEditing(null)}
          match={editing}
          matchLabel={`${sideText(editing.team1Ids)} vs ${sideText(editing.team2Ids)}`}
          courts={courtsQuery.data ?? []}
        />
      )}
    </div>
  );
}
